/**
 * sendOTP — RPC contract for the email-OTP send callable.
 *
 * Cross-process contract between `web/src/auth/session.api.ts`
 * (caller) and `firebase/src/auth/sendOTP.api.ts` (the handler, an action on the
 * `identityOtp` router — `identityOtp.action.ts`).
 * Both ends parse with `.strict()` — an extra key fails the whole request.
 *
 * Fields justified:
 *   orgId  — the organization whose sign-in this is: its members and invitees
 *             are admitted, its key seals the code, it sends. An address it
 *             does not admit but the platform does (a grant elsewhere, a
 *             listed domain) gets a platform-scope code,
 *             since sign-in needs no organization. Absent means platform
 *             scope: the bare host's sign-in, admitting an address that holds
 *             any grant or a listed domain. Nullish rather
 *             than optional so the caller passes its `string | null`
 *             organization cell straight through.
 *   email  — the address to which the 6-digit code is sent; validated as
 *             RFC-5321 email so the handler never receives a malformed
 *             delivery address
 *   locale — the UI language ('de'/'en') the sign-in view is currently
 *             showing; the code email renders in this language. Enum-closed
 *             so an untrusted/malformed value fails the whole-request parse
 *             rather than being silently coerced. Absent → the handler
 *             defaults to 'de'.
 *
 * Output:
 *   sent   — always `true` on success; the response is deliberately generic
 *             and never reveals whether the address exists in the system
 *             (anti-enumeration)
 *   invite — present only when this email holds a live, unexpired, pending
 *             invite — absent on an ordinary sign-in. Resolved server-side
 *             (sendOTP.api.ts) so the wizard can tell the invitee what
 *             they're joining BEFORE they type the code — sendOTP is the
 *             first step where the email is known. The same display
 *             projection the invitee's notification carries
 *             (`Invite.InviteProjectionSchema`): no raw id ever crosses to
 *             the client.
 *
 * Excluded:
 *   userId         — caller is unauthenticated at this point; no session
 *   code           — server-generated and never echoed back
 */
import { LocaleEnum } from "@momo/i18n";
import { z } from "zod";
import { InviteProjectionSchema } from "./invite.entity.ts";
import { Id } from "./workspace.entity.ts";

export const SendOTPInput = z
	.object({
		orgId: Id.nullish(),
		email: z
			.string()
			.email()
			.describe("the delivery address for the one-time code"),
		locale: LocaleEnum.nullish(),
	})
	.strict();
export type SendOTPInput = z.infer<typeof SendOTPInput>;

export const SendOTPOutput = z
	.object({
		sent: z.literal(true).describe("success flag — always true on dispatch"),
		invite: InviteProjectionSchema.nullish().describe(
			"present only when this email holds a live pending invite; absent on an ordinary sign-in",
		),
	})
	.strict();
export type SendOTPOutput = z.infer<typeof SendOTPOutput>;
